<template>
  <div>
    <div class="section">
      <div class="section-title d-flex align-items-center">
        <span class="border-right pr-2 fs-h3 text-uppercase">Tárgyak párosítása</span>
        <span class="small ml-2 w-50">A következő tárgyakat rendszerünk nem tudta automatikusan összekapcsolni az eredményre váró kampányokkal.</span>
      </div>
      <div class="section-content">
        <model-table :rows="mailingsData">
          <template v-slot:td-import_row="{row}">
            <span class="font-weight-bold">{{ row.import_row }}</span>
          </template>
          <template v-slot:td-subject="{row}">
            <span class="font-weight-bold">{{ row.subject }}</span>
          </template>
          <template v-slot:td-campaign="{row}">
            <b-select
              class="test-select"
              :value="row.campaign_id"
              @input="changeSelection(row, $event, row.test_id)"
              :key="dirty"
              :state="row.campaign_id !== -1 && row.test_id !== -1 ? true : null"
            >
              <b-form-select-option
                v-for="(campaign, index) in campaignsData"
                :key="`opt${index}`"
                :value="campaign.value"
                :disabled="campaign.disabled"
                :already-selected="isSelected(campaign.value)"
              >{{ campaign.text }}</b-form-select-option>
            </b-select>
          </template>
          <template v-slot:td-test="{row}">
            <b-select
              class="test-select"
              :value="row.test_id"
              @input="changeSelection(row, row.campaign_id, $event)"
              :options="testsData[row.campaign_id]"
              :disabled="row.campaign_id === -1"
              :key="dirty"
              :state="row.campaign_id !== -1 && row.test_id !== -1 ? true : (row.campaign_id !== -1 && row.test_id === -1 ? false : null)"
            ></b-select>
          </template>
          <template v-slot:td-actions="{row, index}">
            <b-button
              class="mt-1"
              size="sm"
              variant="danger"
              @click="confirmDelete(row, index)"
              v-b-tooltip.hover.noninteractive
              title="Törlés"
            >
              <i class="fas fa-trash"></i>
            </b-button>
          </template>
        </model-table>
      </div>
    </div>
    <div class="w-100 mt-3 d-flex">
        <a class="btn btn-secondary mr-auto" :href="$url('imports.index')">
          <i class="fas fa-arrow-left"></i> Vissza az importálásokhoz
        </a>
        <button
          class="btn btn-info"
          @click="save"
          :disabled="mailingsData.length > 0 && (saving || !isValid)"
        >
          <span v-if="mailingsData.length > 0 && hasPairing">
            Mentés és tovább az eredményekhez <i class="fas fa-arrow-right"></i>
          </span>
          <span v-if="!(mailingsData.length > 0 && hasPairing)">
            Tovább az eredményekhez <i class="fas fa-arrow-right"></i>
          </span>
        </button>
    </div>
  </div>
</template>
<style lang="scss">
  th, td {
    &.column-test {
      min-width: 30%;
      width: 30%;
    }
    &.column-campaign {
      min-width: 30%;
      width: 30%;
    }
  }

  .test-select {
    // The select element is set to required
    // as long as the selected options value
    // is empty the element is not valid.
    &:invalid, [disabled] {
      color: #999;
      background-color: #EEE;
    }

    option {
      color: black;
      background-color: transparent;

      &[already-selected] {
        background-color: var(--lingusta-green-subtle);
        color: #eee;
      }
    }
  }
</style>
<script>
import ErrorHandler from '../../utils/mixins/ErrorHandler';
import ModelTable from '../../utils/mixins/ModelTable';

export default {
  mixins: [
    ErrorHandler()
  ],
  components: {
    'model-table': ModelTable({
      columns: [{
        value: 'import_row',
        text: 'Sor'
      }, {
        value: 'subject',
        text: 'Tárgysor az importált adatokban',
      }, {
        value: 'campaign',
        text: 'Kampány'
      }, {
        value: 'test',
        text: 'Tárgy'
      }, {
        value: 'actions',
        text: 'Műveletek'
      }]
    })
  },
  props: {
    importData: {
      type: Object,
      required: true,
      default: () => ({
        id: -1
      })
    },
    mailings: {
      type: Array,
      required: true,
      default: () => ([])
    },
    campaigns: {
      type: Array,
      required: true,
      default: () => ([])
    },
  },
  data() {
    return {
      mailingsData: [],
      campaignsData: [],
      testsData: {},
      dirty: false,
      saving: false
    }
  },
  computed: {
    isValid() {
      return _.reduce(this.mailingsData, (result, mailing) => {
        // a row is valid if the campaign is not set or both the campaign and test is set
        return result && (mailing.campaign_id === -1 || (mailing.campaign_id !== -1 && mailing.test_id !== -1))
      }, true)
    },
    hasPairing() {
      return this.mailingsData.length === 0 || _.reduce(this.mailingsData, (result, mailing) => {
        return result || (mailing.campaign_id !== -1 && mailing.test_id !== -1)
      }, false)
    }
  },
  watch: {
    mailings: {
      immediate: true,
      handler(mailings) {
        this.mailingsData = _.map(mailings, mailing => ({
          ...mailing,
          campaign_id: -1,
          test_id: -1,
        }))
      }
    },
    campaigns: {
      immediate: true,
      handler(campaigns) {
        // the empty options
        var empty = {
          text: '- nincs kiválasztva -',
          disabled: false,
          value: -1,
        }

        this.campaignsData = _.map(campaigns, campaign => {
          // map tests to a list per campaign
          _.forEach(campaign.tests, test => {
            this.testsData[campaign.id] = _.map(campaign.tests, test => ({
              text: test.subject,
              value: test.id,
              disabled: false
            }))

            // add an empty option to each
            this.testsData[campaign.id].splice(0, 0, empty)
          })

          // map the campaign too
          return {
            disabled: false,
            text: campaign.name,
            value: campaign.id
          }
        })

        // add an empty campaign option
        this.campaignsData.splice(0, 0, empty)

        // the empty campaign option has an empty option too
        this.testsData[-1] = [empty]
      }
    },
  },
  mounted() {
  },
  methods: {
    confirmDelete(mailing, index) {
      this.$bvModal.msgBoxConfirm(`Biztosan törölni szeretnéd a következő tárgyat: "${mailing.subject}"?`, {
        title: 'Megerősítés',
        okVariant: 'danger',
        okTitle: 'Igen',
        cancelTitle: 'Mégsem'
      }).then(value => {
        if(value) {
          this.$http.delete(this.$url('api.mailings.destroy', mailing.id))
            .then(() => {
              this.changeSelection(this.mailingsData[index], -1, -1)
              this.mailingsData.splice(index, 1)
            })
            .catch(() => {
              this.$bvToast.toast('Hiba történt a művelet végrehajtása közben!', {
                title: 'Hiba',
                variant: 'danger'
              })
            })
        }
      })
    },
    save() {
      if (this.mailingsData.length === 0 || !this.hasPairing) {
        window.location = this.$url('statistics.index')
        return
      }

      this.saving = false
      this.$http.put(this.$url('api.imports.pair', this.importData.id), {
        mailings: _.filter(this.mailingsData, mailing => mailing.test_id !== -1)
          .map(mailing => ({
            id: mailing.id,
            test_id: mailing.test_id,
          }))
      })
        .then(() => {
          window.location = this.$url('statistics.index')
        })
        .catch(this.handleErrors)
    },
    isSelected(campaignId) {
      if(campaignId === -1) {
        return false
      }

      var found = _.find(this.mailingsData, mailing => {
        return mailing.campaign_id === campaignId
      })

      return typeof found !== 'undefined'
    },
    /**
     * Change the selection for the given row
     */
    changeSelection(row, campaignId, testId) {
      // used to force re-render every select
      this.dirty = !this.dirty

      // determine which property changed
      var campaignChanged = campaignId !== row.campaign_id
      var testChanged = testId !== row.test_id

      // mark the old campaign selection as not disabled
      var oldCamapign = _.find(this.campaignsData, campaign => campaign.value === row.campaign_id)
      if(typeof oldCamapign !== 'undefined') {
        oldCamapign.disabled = false
      }

      // mark the old test selection as not disabled
      if(row.campaign_id !== -1) {
        var oldTest = _.find(this.testsData[row.campaign_id], test => test.value === row.test_id)
        if(typeof oldTest !== 'undefined') {
          oldTest.disabled = false
        }
      }

      // make the changes
      row.campaign_id = campaignId
      row.test_id = testId

      // if the campaign changed change the test to the empty option
      if(campaignChanged) {
        row.test_id = -1
        testId = -1
        testChanged = true
      }

      // if neither the campaign nor the test selection is empty
      if(testId !== -1 && campaignId !== -1) {
        // mark the new test selection as disabled
        var newTest = _.find(this.testsData[campaignId], test => test.value === testId)
        if(typeof newTest !== 'undefined') {
          newTest.disabled = true
        }
      }

      // if the campaign is not empty
      if(campaignId !== -1) {
        // find the newly selected campaign
        var newCampaign = _.find(this.campaignsData, campaign => campaign.value === campaignId)
        if(typeof newCampaign !== 'undefined') {
          // mark the campaign as disabled if every not empty test related to it are disabled
          newCampaign.disabled = _.reduce(this.testsData[campaignId], (disabled, test) => {
            return disabled && (test.value === -1 || test.disabled)
          }, true)
        }
      }
    },
  }
}
</script>